'use client';

import { useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { PricingTable } from './pricing-table';
import { FAQSection } from './faq-section';

interface PublicPricingClientProps {
  isAuthenticated: boolean;
  currentPlanCode?: 'individual' | 'enterprise_1' | 'enterprise_5' | 'pay_as_you_go' | null;
}

type Cadence = 'monthly' | 'annual';

export function PublicPricingClient({
  isAuthenticated,
  currentPlanCode = null,
}: PublicPricingClientProps) {
  const t = useTranslations('landing.pricing');
  const router = useRouter();

  const handleSelectPlan = useCallback(
    async (planCode: string, cadence: Cadence) => {
      if (isAuthenticated) {
        if (currentPlanCode && currentPlanCode === planCode) {
          return;
        }

        const params = new URLSearchParams({
          plan: planCode,
          cadence,
        });
        router.push(`/upgrade?${params.toString()}`);
      } else {
        // Anonymous users go to signup with intent preserved
        const params = new URLSearchParams({
          mode: 'signup',
          plan: planCode,
          cadence,
          redirect: '/onboarding',
        });
        router.push(`/login?${params.toString()}`);
      }
    },
    [currentPlanCode, isAuthenticated, router],
  );

  return (
    <div className="space-y-16">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-4xl font-serif text-white mb-4">{t('publicHeader.title')}</h1>
        <p className="text-lg text-white/50 max-w-2xl mx-auto">{t('publicHeader.subtitle')}</p>
      </div>

      {/* Pricing Table */}
      <PricingTable
        onSelectPlan={handleSelectPlan}
        isAuthenticated={isAuthenticated}
        currentPlanCode={currentPlanCode}
      />

      {/* Money-back guarantee */}
      <div className="text-center text-sm text-white/40">
        <p>{t('publicHeader.guarantee')}</p>
      </div>

      {/* FAQ */}
      <FAQSection />

      {/* CTA */}
      <div className="text-center pb-8">
        <p className="text-white/50 mb-4">{t('publicHeader.stillHaveQuestions')}</p>
        <Link href="/contact" className="text-primary hover:underline">
          {t('publicHeader.contactSupport')}
        </Link>
      </div>
    </div>
  );
}
